import React, { useState, useEffect } from 'react';
import './Historique.css';
import DashboardHeader from '../components/DashboardHeader';
import ParentSidebar from '../components/ParentSidebar';
import { BookIcon, DocumentIcon, FoodIcon, BusIcon, MoneyIcon, IdCardIcon, ClipboardIcon, CreditCardIcon } from '../components/Icons';

function Historique() {
  const [paiements, setPaiements] = useState([]);
  const [statistiques, setStatistiques] = useState(null);
  const [etudiant, setEtudiant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(''); // Pour la recherche

  useEffect(() => {
    fetchHistorique();
  }, []);

  const fetchHistorique = async () => {
    try {
      setLoading(true);
      const userEmail = localStorage.getItem('user_email');
      
      const response = await fetch(`http://127.0.0.1:8000/api/v1/paiements/historique?email=${userEmail}`);
      const data = await response.json();
      
      if (response.ok) {
        setPaiements(data.paiements);
        setStatistiques(data.statistiques);
        setEtudiant(data.etudiant);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
      setLoading(false);
    }
  };

  const handleNewPayment = () => {
    window.navigateTo && window.navigateTo('payement');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montant);
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'valide': { class: 'badge-success', text: '✓ Validé', icon: '✓' },
      'en_attente': { class: 'badge-warning', text: '⏳ En attente', icon: '⏳' },
      'refuse': { class: 'badge-danger', text: '✗ Refusé', icon: '✗' }
    };
    return badges[statut] || badges['en_attente'];
  };

  const getTypeIcon = (type) => {
    const iconProps = { size: 20, color: "#0078D7" };
    const icons = {
      'scolarite': <BookIcon {...iconProps} />,
      'inscription': <DocumentIcon {...iconProps} />,
      'cantine': <FoodIcon {...iconProps} />,
      'transport': <BusIcon {...iconProps} />,
      'sport': <MoneyIcon {...iconProps} />,
      'uniforme': <MoneyIcon {...iconProps} />,
      'carte_scolaire': <IdCardIcon {...iconProps} />,
      'autre': <MoneyIcon {...iconProps} />
    };
    return icons[type] || <MoneyIcon {...iconProps} />;
  };

  const filteredPaiements = paiements.filter(p => {
    if (!filter) return true;
    const search = filter.toLowerCase();
    return (
      p.type?.toLowerCase().includes(search) ||
      p.reference?.toLowerCase().includes(search) ||
      p.mode_paiement?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="admin-layout">
        <DashboardHeader />
        <div className="admin-container">
          <ParentSidebar activePage="historique" />
          <main className="parent-main">
            <div className="loading-state">Chargement de l'historique...</div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <div className="admin-container">
        <ParentSidebar activePage="historique" />
        <main className="parent-main">
          <div className="historique-content">
            {/* Statistiques */}
            {statistiques && (
              <div className="stats-cards">
                <div className="stat-card stat-success">
                  <div className="stat-icon"><MoneyIcon size={32} color="#fff" /></div>
                  <div className="stat-info">
                    <div className="stat-label">Total Payé</div>
                    <div className="stat-value">{formatMontant(statistiques.total_paye)} FCFA</div>
                    <div className="stat-detail">{statistiques.nombre_paiements} paiement(s)</div>
                  </div>
                </div>
              </div>
            )}

            <div className="historique-card">
              <div className="historique-header">
                <div className="header-left">
                  <h2>Historique des Paiements</h2>
                  {etudiant && (
                    <p className="etudiant-info">
                      {etudiant.prenom} {etudiant.nom} - {etudiant.classe}
                    </p>
                  )}
                </div>
                <button className="new-payment-btn" onClick={handleNewPayment}>
                  <CreditCardIcon size={20} /> Effectuer un paiement
                </button>
              </div>

              {/* Recherche */}
              <div className="filters-bar">
                <input
                  type="text"
                  placeholder="Rechercher par type, référence ou mode de paiement..."
                  className="search-input"
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>

              {filteredPaiements.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon"><ClipboardIcon size={48} color="#999" /></div>
                  <h3>Aucun paiement</h3>
                  <p>Aucun paiement trouvé pour ce filtre.</p>
                  <button className="btn-primary" onClick={handleNewPayment}>
                    Effectuer un premier paiement
                  </button>
                </div>
              ) : (
                <div className="transactions-table">
                  <div className="table-header">
                    <div className="col-type">Type</div>
                    <div className="col-reference">Référence</div>
                    <div className="col-date">Date & Heure</div>
                    <div className="col-mode">Mode</div>
                    <div className="col-montant">Montant</div>
                  </div>

                  <div className="table-body">
                    {filteredPaiements.map((paiement) => {
                      return (
                        <div key={paiement.id} className="transaction-row">
                          <div className="col-type">
                            <div className="type-info">
                              <span className="type-icon">{getTypeIcon(paiement.type)}</span>
                              <div>
                                <div className="type-name">{paiement.type}</div>
                                {paiement.trimestre && (
                                  <div className="type-detail">{paiement.trimestre}</div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-reference">
                            <span className="reference-code">{paiement.reference}</span>
                          </div>
                          <div className="col-date">{formatDate(paiement.date_paiement)}</div>
                          <div className="col-mode">
                            <span className="mode-badge">{paiement.mode_paiement}</span>
                          </div>
                          <div className="col-montant">
                            <span className="montant-value">{formatMontant(paiement.montant)} FCFA</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Historique;
